import React, { useEffect, useRef } from 'react';
import { useSigma } from '@react-sigma/core';
import { CameraState } from 'sigma/types';
import { EdgeAttributes, NodeAttributes } from './graphTypes';

// Browsers cap the number of live WebGL contexts (Chrome/WebKit: 16 per
// process, oldest evicted) and may drop contexts of background tabs under GPU
// pressure. Sigma 3 renders through three WebGL canvases (edges, nodes,
// hoverNodes) and never listens for `webglcontextlost`: every draw call turns
// into a silent no-op, so the canvas stays blank while the surrounding panels
// keep working — the "two graph tabs, one goes black" bug.
//
// This child of SigmaContainer reports the loss (once per instance, with the
// camera state so the view survives) and lets the page remount the container;
// on the new instance it re-applies the camera the user had.
export const WebGLContextLossRecovery: React.FC<{
  onContextLost: (camera: CameraState) => void;
  // camera to restore on this (freshly remounted) instance, if any
  restoreCamera: React.MutableRefObject<CameraState | null>;
}> = ({ onContextLost, restoreCamera }) => {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();
  const onContextLostRef = useRef(onContextLost);
  onContextLostRef.current = onContextLost;

  useEffect(() => {
    const pending = restoreCamera.current;
    if (pending) {
      restoreCamera.current = null;
      sigma.getCamera().setState(pending);
    }
  }, [sigma, restoreCamera]);

  useEffect(() => {
    // the three WebGL layers usually lose their contexts together — report once
    let reported = false;
    const handleLost = () => {
      if (reported) return;
      reported = true;
      onContextLostRef.current(sigma.getCamera().getState());
    };
    const canvases = Object.values(sigma.getCanvases());
    canvases.forEach((canvas) =>
      canvas.addEventListener('webglcontextlost', handleLost),
    );
    return () => {
      canvases.forEach((canvas) =>
        canvas.removeEventListener('webglcontextlost', handleLost),
      );
    };
  }, [sigma]);

  return null;
};
